import { T } from '@salecycle/experimental/Typography/Typography';
import {
  FormGroup,
  Input,
  ISelectOption,
  RadioButton,
  Select,
  SingleDatePicker,
} from '@salecycle/ui-component-library';
import { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment-timezone';
import {
  TargetAudienceType,
  TargetLookbackPeriod,
} from 'sections/bulk-retargeting-campaigns/CampaignEditor/state/CampaignEditorState';
import styled, { useTheme } from 'styled-components';
import { usePreferencesState } from 'core/preferences/usePreferencesContext';
import { useFeatureFlags } from 'core/app/AppContext';
import {
  clampValue,
  getMaxDays,
  getMaxMonths,
  getMaxAllowedEndDate,
  getMinAllowedDate,
  validateAndNormalizeStartDate,
  validateAndNormalizeEndDate,
} from 'sections/bulk-retargeting-campaigns/CampaignEditor/utils/dateValidation';
import useNotifications from 'core/notifications/useNotifications';

const SegmentBackground = styled.div`
  background-color: ${({ theme }) => theme.designToken.neutral50};
  padding: ${({ theme }) => theme.designToken.spacing5};
  display: flex;
  gap: ${({ theme }) => theme.designToken.spacing2};
  align-items: center;
`;

const ItalicHelperText = styled.div`
  font-style: italic;
`;

const createTargetAudienceOptions = (t: TFunction): ISelectOption<TargetAudienceType>[] => [
  {
    label: t('bulkRetargetingCampaigns.editor.targetAudienceTypes.' + TargetAudienceType.BasketAbandonment),
    value: TargetAudienceType.BasketAbandonment,
  },
  {
    label: t('bulkRetargetingCampaigns.editor.targetAudienceTypes.' + TargetAudienceType.BrowseAbandonment),
    value: TargetAudienceType.BrowseAbandonment,
  },
  {
    label: t('bulkRetargetingCampaigns.editor.targetAudienceTypes.' + TargetAudienceType.Purchase),
    value: TargetAudienceType.Purchase,
  },
];

const createTargetLookbackLength = (t: TFunction): ISelectOption<TargetLookbackPeriod>[] => [
  {
    label: t('bulkRetargetingCampaigns.editor.targetLookbackPeriod.days'),
    value: TargetLookbackPeriod.Days,
  },
  {
    label: t('bulkRetargetingCampaigns.editor.targetLookbackPeriod.months'),
    value: TargetLookbackPeriod.Months,
  },
];

export interface TargetAudienceProps {
  onChange: (targetAudience: {
    audience: TargetAudienceType;
    days: number;
    dateType?: TargetLookbackPeriod;
    startDate?: Moment;
    endDate?: Moment;
    isUsingDateRange?: boolean;
  }) => void;
  value: {
    audience: TargetAudienceType;
    days: number;
    dateType?: TargetLookbackPeriod;
    startDate?: Moment;
    endDate?: Moment;
    isUsingDateRange?: boolean;
  };
  disabled?: boolean;
}

export const TargetAudience = ({ onChange, value, disabled }: TargetAudienceProps) => {
  const { t } = useTranslation('campaigns');
  const theme = useTheme();

  const { flagEnabled } = useFeatureFlags();
  const featureBulkDatePickers = flagEnabled('ld-bulk-date-pickers');

  const options = useMemo(() => createTargetAudienceOptions(t), [t]);
  const dateMonthOptions = useMemo(() => createTargetLookbackLength(t), [t]);

  const [usingDateRange, setUsingDateRange] = useState(value.isUsingDateRange ? value.isUsingDateRange : false);

  const [isStartDateFocused, setIsStartDateFocused] = useState(false);
  const [isEndDateFocused, setIsEndDateFocused] = useState(false);

  const [actualNumberOfDays, setActualNumberOfDays] = useState(value.days);

  const { timezone } = usePreferencesState();

  const [startDateMoment, setStartDateMoment] = useState(value.startDate);
  const [endDateMoment, setEndDateMoment] = useState(value.endDate);

  // supporting methods for Bulk retargeting date range picker feature and months, days logic implementation
  const minAllowedDate = useMemo(() => getMinAllowedDate(timezone), [timezone]);
  const maxAllowedEndDate = useMemo(() => {
    if (!startDateMoment) {
      return undefined;
    }
    return getMaxAllowedEndDate(startDateMoment);
  }, [startDateMoment]);

  const maxDays = useMemo(() => getMaxDays(timezone), [timezone]);
  const maxMonths = useMemo(() => getMaxMonths(timezone), [timezone]);

  const { showNotification } = useNotifications();

  useEffect(() => {
    if (value.isUsingDateRange && startDateMoment && endDateMoment) {
      setActualNumberOfDays(endDateMoment.diff(startDateMoment, 'days'));
    } else {
      setActualNumberOfDays(value.days * (value.dateType === TargetLookbackPeriod.Months ? 30 : 1));
    }
  }, [endDateMoment, startDateMoment, value.dateType, value.days, value.isUsingDateRange]);

  const onSelectedAudienceChanged = useCallback(
    (selected: ISelectOption<TargetAudienceType>) => {
      onChange({
        audience: selected.value,
        days: value.days,
        dateType: value.dateType,
        startDate: startDateMoment,
        endDate: endDateMoment,
        isUsingDateRange: usingDateRange,
      });
    },
    [onChange, value.days, value.dateType, startDateMoment, endDateMoment, usingDateRange],
  );

  const onSelectedDateTypeChanged = useCallback(
    (selected: ISelectOption<TargetLookbackPeriod>) => {
      let newDays = value.days;
      if (selected.value === TargetLookbackPeriod.Months && newDays > maxMonths) {
        newDays = maxMonths;
      }
      onChange({
        audience: value.audience,
        days: newDays,
        dateType: selected.value,
        startDate: startDateMoment,
        endDate: endDateMoment,
        isUsingDateRange: usingDateRange,
      });
    },
    [value.days, value.audience, maxMonths, onChange, startDateMoment, endDateMoment, usingDateRange],
  );

  const selectedAudience: ISelectOption<TargetAudienceType> = {
    value: value.audience,
    label: t('bulkRetargetingCampaigns.editor.targetAudienceTypes.' + value.audience),
  };

  const onSelectedDaysChanged = useCallback(
    (newValue: string) => {
      // Default to Days if dateType is undefined to prevent incorrect clamping
      const dateType = value.dateType || TargetLookbackPeriod.Days;
      const maxValue = dateType === TargetLookbackPeriod.Days ? maxDays : maxMonths;
      const clampedValue = clampValue(newValue, maxValue);

      onChange({
        audience: value.audience,
        days: clampedValue,
        dateType: dateType,
        startDate: startDateMoment,
        endDate: endDateMoment,
        isUsingDateRange: usingDateRange,
      });
    },
    [value.dateType, value.audience, maxDays, maxMonths, onChange, startDateMoment, endDateMoment, usingDateRange],
  );

  const onEndDateAudienceChanged = useCallback(
    (newEndDate: any) => {
      if (!newEndDate) {
        onChange({
          audience: value.audience,
          days: value.days,
          dateType: value.dateType,
          startDate: startDateMoment,
          endDate: null,
          isUsingDateRange: usingDateRange,
        });
        return;
      }

      // Clamp the selected date to maxAllowedEndDate if it exceeds it
      let dateToValidate = newEndDate;
      if (startDateMoment && newEndDate.isAfter(maxAllowedEndDate, 'day')) {
        dateToValidate = maxAllowedEndDate;
      }

      const normalizedEndDate = validateAndNormalizeEndDate(
        dateToValidate,
        startDateMoment,
        minAllowedDate,
        maxAllowedEndDate,
        timezone,
      );

      // If validation failed, don't update
      if (!normalizedEndDate) {
        return;
      }

      onChange({
        audience: value.audience,
        days: value.days,
        dateType: value.dateType,
        startDate: startDateMoment,
        endDate: normalizedEndDate,
        isUsingDateRange: usingDateRange,
      });
    },
    [
      startDateMoment,
      maxAllowedEndDate,
      minAllowedDate,
      timezone,
      onChange,
      value.audience,
      value.days,
      value.dateType,
      usingDateRange,
    ],
  );

  const onStartDateAudienceChanged = useCallback(
    (newStartDate: any) => {
      const normalizedStartDate = validateAndNormalizeStartDate(newStartDate, endDateMoment, minAllowedDate, timezone);

      // If validation failed, don't update
      if (newStartDate && !normalizedStartDate) {
        return;
      }

      let finalEndDate = endDateMoment;

      // If there's an existing end date and a new start date, check if it exceeds the 180 day limit
      if (normalizedStartDate && endDateMoment) {
        const daysDifference = endDateMoment.diff(normalizedStartDate, 'days');
        if (daysDifference > 180) {
          // Auto-adjust the end date to be within the 180 day range
          const maxAllowedEndDate = normalizedStartDate.clone().add(180, 'days');
          finalEndDate = maxAllowedEndDate.clone().endOf('day');
          setEndDateMoment(finalEndDate);
          showNotification(
            t('bulkRetargetingCampaigns.editor.endDateValidation.' + value.audience),
            'secondary',
            10000,
          );
        }
      }

      onChange({
        audience: value.audience,
        days: value.days,
        dateType: value.dateType,
        startDate: normalizedStartDate,
        endDate: finalEndDate,
        isUsingDateRange: usingDateRange,
      });
    },
    [
      timezone,
      minAllowedDate,
      endDateMoment,
      onChange,
      value.audience,
      value.days,
      value.dateType,
      usingDateRange,
      showNotification,
      t,
    ],
  );

  const onWithinClick = () => {
    setUsingDateRange(false);
    onChange({
      audience: value.audience,
      days: value.days,
      dateType: value.dateType,
      startDate: startDateMoment,
      endDate: endDateMoment,
      isUsingDateRange: false,
    });
  };

  const onBetweenClick = () => {
    setUsingDateRange(true);
    onChange({
      audience: value.audience,
      days: value.days,
      dateType: value.dateType,
      startDate: startDateMoment,
      endDate: endDateMoment,
      isUsingDateRange: true,
    });
  };

  const selectedUnit: ISelectOption<TargetLookbackPeriod> = useMemo(() => {
    // Always ensure we have a valid unit, default to Days if not set
    const unit = value.dateType || TargetLookbackPeriod.Days;
    const isSingular = value.days === 1;

    // Find the base option to ensure the value property matches (React Select matches by value, not label)
    const baseOption = dateMonthOptions.find(option => option.value === unit) || dateMonthOptions[0];

    // Customize the label based on singular/plural while keeping the same value property
    // This ensures React Select can match it while displaying the correct singular/plural form
    if (unit === TargetLookbackPeriod.Days) {
      return {
        ...baseOption,
        label: isSingular
          ? t('bulkRetargetingCampaigns.editor.targetLookbackPeriod.day')
          : t('bulkRetargetingCampaigns.editor.targetLookbackPeriod.days'),
      };
    } else if (unit === TargetLookbackPeriod.Months) {
      return {
        ...baseOption,
        label: isSingular
          ? t('bulkRetargetingCampaigns.editor.targetLookbackPeriod.month')
          : t('bulkRetargetingCampaigns.editor.targetLookbackPeriod.months'),
      };
    }

    return baseOption;
  }, [value.dateType, value.days, dateMonthOptions, t]);

  return (
    <div style={{ display: 'flex', gap: theme.designToken.spacing5, flexDirection: 'column', width: '100%' }}>
      <SegmentBackground>
        {featureBulkDatePickers === true ? (
          <FormGroup>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.designToken.spacing5 }}>
              <span
                style={{
                  display: 'flex',
                  gap: theme.designToken.spacing5,
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <T>{t('bulkRetargetingCampaigns.editor.targetAudienceSegmentation.sendTo')}</T>
                <span style={{ width: 256, height: 'fit-content' }}>
                  <Select
                    inputId="target-audience-audience"
                    inline
                    spacingBelow={false}
                    value={selectedAudience}
                    onChange={onSelectedAudienceChanged}
                    options={options}
                    ariaLabel={t('bulkRetargetingCampaigns.editor.targetAudienceSegmentationAria')}
                    disabled={disabled}
                  />
                </span>
              </span>
              <span
                style={{
                  display: 'flex',
                  gap: theme.designToken.spacing5,
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <RadioButton selected={!usingDateRange} onClick={onWithinClick} disabled={disabled} />
                <T>{t('bulkRetargetingCampaigns.editor.targetAudienceSegmentation.within')}</T>
                <span style={{ width: 88, height: 'fit-content' }}>
                  <Input
                    inputId="target-audience-days"
                    type="number"
                    spacingBelow={false}
                    max={value.dateType === TargetLookbackPeriod.Months ? String(maxMonths) : String(maxDays)}
                    min="1"
                    value={value.days}
                    onChange={onSelectedDaysChanged}
                    aria-label={t('bulkRetargetingCampaigns.editor.targetAudienceDaysAria')}
                    disabled={disabled || usingDateRange}
                  />
                </span>
                <span style={{ width: 128, height: 'fit-content' }}>
                  <Select
                    inputId="target-lookback-period"
                    inline
                    spacingBelow={false}
                    value={selectedUnit}
                    onChange={onSelectedDateTypeChanged}
                    options={dateMonthOptions}
                    ariaLabel={t('bulkRetargetingCampaigns.editor.dayMonthSelectionAria')}
                    disabled={disabled}
                  />
                </span>
                <T>{t('bulkRetargetingCampaigns.editor.targetAudienceSegmentation.prior')}</T>
                {!value.isUsingDateRange && (
                  <ItalicHelperText>
                    {t(
                      actualNumberOfDays === 1
                        ? 'bulkRetargetingCampaigns.editor.targetAudienceSegmentation.italicTextDay'
                        : 'bulkRetargetingCampaigns.editor.targetAudienceSegmentation.italicTextDays',
                      { actualNumberOfDays },
                    )}
                  </ItalicHelperText>
                )}
              </span>
              <span
                style={{ display: 'flex', gap: theme.designToken.spacing5, flexDirection: 'row', alignItems: 'center' }}
              >
                <RadioButton
                  selected={usingDateRange}
                  onClick={onBetweenClick}
                  data-testid="between"
                  disabled={disabled}
                />
                <T>between </T>
                <span data-testid="dateStart">
                  <SingleDatePicker
                    ariaLabel={t('campaigns:dates.start.label')}
                    date={startDateMoment}
                    focused={isStartDateFocused}
                    isMini={false}
                    onDateChange={(selectedDate: Moment) => {
                      if (!selectedDate) {
                        setStartDateMoment(selectedDate);
                        onStartDateAudienceChanged(selectedDate);
                        return;
                      }
                      const startOfDay = moment.tz(`${selectedDate.format('YYYY-MM-DD')}T00:00:00`, timezone);
                      setStartDateMoment(startOfDay);
                      onStartDateAudienceChanged(startOfDay);
                    }}
                    onFocusChange={({ focused }) => setIsStartDateFocused(focused)}
                    placeholder={'DD/MM/YYYY'}
                    spacingBelow={false}
                    minDate={minAllowedDate}
                    maxDate={endDateMoment}
                    disabled={disabled || !usingDateRange}
                    width={140}
                  />
                </span>
                <T> and </T>
                <span data-testid="dateEnd">
                  <SingleDatePicker
                    ariaLabel={t('campaigns:dates.end.label')}
                    date={endDateMoment}
                    focused={isEndDateFocused}
                    isMini={false}
                    onDateChange={(selectedDate: Moment) => {
                      if (!selectedDate) {
                        setEndDateMoment(selectedDate);
                        onEndDateAudienceChanged(selectedDate);
                        return;
                      }
                      const endOfDay = moment.tz(`${selectedDate.format('YYYY-MM-DD')}T23:59:59`, timezone);
                      setEndDateMoment(endOfDay);
                      onEndDateAudienceChanged(endOfDay);
                    }}
                    onFocusChange={({ focused }) => setIsEndDateFocused(focused)}
                    placeholder={'DD/MM/YYYY'}
                    spacingBelow={false}
                    minDate={startDateMoment}
                    maxDate={maxAllowedEndDate}
                    disabled={disabled || !usingDateRange}
                    width={140}
                  />
                </span>
                {value.isUsingDateRange && startDateMoment && endDateMoment && (
                  <ItalicHelperText>
                    {t(
                      actualNumberOfDays === 1
                        ? 'bulkRetargetingCampaigns.editor.targetAudienceSegmentation.italicTextDay'
                        : 'bulkRetargetingCampaigns.editor.targetAudienceSegmentation.italicTextDays',
                      { actualNumberOfDays },
                    )}
                  </ItalicHelperText>
                )}
              </span>
            </div>
          </FormGroup>
        ) : (
          <>
            <T>{t('bulkRetargetingCampaigns.editor.targetAudienceSegmentation.sendTo')}</T>
            <span style={{ width: 256, height: 'fit-content' }}>
              <Select
                inputId="target-audience-audience"
                inline
                spacingBelow={false}
                value={selectedAudience}
                onChange={onSelectedAudienceChanged}
                options={options}
                ariaLabel={t('bulkRetargetingCampaigns.editor.targetAudienceSegmentationAria')}
                disabled={disabled}
              />
            </span>
            <T>{t('bulkRetargetingCampaigns.editor.targetAudienceSegmentation.within')}</T>
            <span style={{ width: 88, height: 'fit-content' }}>
              <Input
                inputId="target-audience-days"
                type="number"
                spacingBelow={false}
                max="30"
                min="1"
                value={value.days}
                onChange={onSelectedDaysChanged}
                aria-label={t('bulkRetargetingCampaigns.editor.targetAudienceDaysAria')}
                disabled={disabled}
              />
            </span>
            <T>{t('bulkRetargetingCampaigns.editor.targetAudienceSegmentation.daysPrior')}</T>
          </>
        )}
      </SegmentBackground>
    </div>
  );
};
